import React, { useState, useRef, useEffect } from "react";
import { useShowAlert } from "../../../../context/AlertContext";
import AxiosInstance from "../../../../utils/AxiosInstance";
import handleError from "../../../../constants/handleError";
import { CheckIcon, EditIcon, LniClosIcon } from "../../../../constants/Icons";

const EditPassword = ({
  onClose,
  onUpdatePassword,
  onUpdateDetails,
  editData,
}) => {
  const showAlert = useShowAlert();

  const [formData, setFormData] = useState({
    domain: "",
    username: "",
    password: "",
    notes: "",
  });

  const [errors, setErrors] = useState({});
  const [editPasswordMode, setEditPasswordMode] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);

  const passwordRef = useRef(null);
  const domainRef = useRef(null);
  const usernameRef = useRef(null);
  const notesRef = useRef(null);
  const updateButtonRef = useRef(null);

  useEffect(() => {
    if (editData) {
      setFormData({
        domain: editData.title || "",
        username: editData.userName || "",
        password: "",
        notes: editData.notes || "",
      });
    }
  }, [editData]);

  const handleChange = (field, value) => {
    // Trim only the "notes" field to avoid accidental spaces
    const trimmedValue = field === "notes" ? value.trimStart() : value;
    setFormData((prev) => ({ ...prev, [field]: trimmedValue }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateField = (field, value) => {
    switch (field) {
      case "domain":
        if (!value) return "Domain is required.";
        break;
      case "username":
        if (!value) return "Username is required.";
        break;
      case "password":
        if (!value) return "Password is required.";
        break;
      case "notes":
        if (value.length > 500) return "Notes cannot exceed 500 characters.";
        break;
      default:
        return "";
    }
    return "";
  };

  const fetchCurrentPassword = async () => {
    try {
      setLoadingPassword(true);
      const response = await AxiosInstance.get(
        `module/password/${editData._id}`
      );
      const fetchedPassword = response.data.results?.password || "";
      setFormData((prev) => ({ ...prev, password: fetchedPassword }));
      setEditPasswordMode(true);
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleUpdatePasswordClick = () => {
    const passwordError = validateField("password", formData.password);
    if (passwordError) {
      setErrors({ password: passwordError });
      showAlert("error", passwordError);
      return;
    }

    onUpdatePassword({ password: formData.password });
    setEditPasswordMode(false);
    setFormData((prev) => ({ ...prev, password: "" }));
  };

  const handleUpdateDetailsClick = () => {
    const detailFields = ["domain", "username", "notes"];
    const newErrors = {};
    const trimmedNotes = formData.notes.trim(); // ensure notes are fully trimmed

    const finalData = {
      ...formData,
      notes: trimmedNotes,
    };

    detailFields.forEach((key) => {
      const error = validateField(key, finalData[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = newErrors[Object.keys(newErrors)[0]];
      showAlert("error", firstError);
      return;
    }

    onUpdateDetails({
      domain: finalData.domain,
      username: finalData.username,
      notes: finalData.notes,
    });
  };

  useEffect(() => {
    if (editPasswordMode && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [editPasswordMode]);

  return (
    <div className="modal-overlay">
      <div className="modal-backdrop show"></div>
      <div
        className="modal show d-block"
        tabIndex="0"
        aria-modal="true"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header">
              <h6 className="modal-title">Edit Password</h6>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>

            {/* Body */}
            <div className="modal-body">
              {/* Password */}
              <div className="mb-3">
                <label className="fw-semibold mb-1">Password</label>
                {!editPasswordMode ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary ms-2"
                    onClick={fetchCurrentPassword}
                    disabled={loadingPassword}
                  >
                    {loadingPassword ? (
                      "Loading..."
                    ) : (
                      <EditIcon/>
                    )}
                  </button>
                ) : (
                  <div className="d-flex align-items-center gap-2">
                    <input
                      ref={passwordRef}
                      type="text"
                      className="form-control"
                      placeholder="Enter new password"
                      value={formData.password}
                      onChange={(e) => handleChange("password", e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleUpdatePasswordClick();
                      }}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={handleUpdatePasswordClick}
                    >
                      <CheckIcon/>
                    </button>
                  </div>
                )}
                {errors.password && (
                  <div className="invalid-feedback d-block">
                    {errors.password}
                  </div>
                )}
              </div>

              {/* Domain */}
              <div className="mb-3">
                <label className="fw-semibold mb-1">Title</label>
                <input
                  ref={domainRef}
                  type="text"
                  className="form-control"
                  value={formData.domain}
                  onChange={(e) => handleChange("domain", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") usernameRef.current?.focus();
                  }}
                />
                {errors.domain && (
                  <div className="invalid-feedback d-block">
                    {errors.domain}
                  </div>
                )}
              </div>

              {/* Username */}
              <div className="mb-3">
                <label className="fw-semibold mb-1">Username</label>
                <input
                  ref={usernameRef}
                  type="text"
                  className="form-control"
                  value={formData.username}
                  onChange={(e) => handleChange("username", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") notesRef.current?.focus();
                  }}
                />
                {errors.username && (
                  <div className="invalid-feedback d-block">
                    {errors.username}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="mb-3">
                <label className="fw-semibold mb-1">Notes</label>
                <textarea
                  ref={notesRef}
                  className="form-control"
                  rows={4}
                  maxLength={500}
                  value={formData.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      updateButtonRef.current?.click();
                    }
                  }}
                />
                {errors.notes && (
                  <div className="invalid-feedback d-block">{errors.notes}</div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer d-flex justify-content-between">
              <button className="btn btn-outline-secondary" onClick={onClose}>
                <LniClosIcon iClass={"fs-15"} /> Cancel
              </button>
              <button
                ref={updateButtonRef}
                className="btn btn-primary"
                onClick={handleUpdateDetailsClick}
              >
                <CheckIcon/> Update Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPassword;
